import React, { useEffect, useState, useRef } from "react";
import { useNetworkStatus } from "../../hooks/useNetworkStatus";

const STYLE_ID = "__offline-overlay-styles__";
const KEYFRAMES = `
  @keyframes __ol-ping1__ {
    0%   { transform: scale(1);   opacity: 0.55; }
    80%  { transform: scale(2.4); opacity: 0; }
    100% { transform: scale(2.4); opacity: 0; }
  }
  @keyframes __ol-ping2__ {
    0%   { transform: scale(1);   opacity: 0.35; }
    80%  { transform: scale(3.2); opacity: 0; }
    100% { transform: scale(3.2); opacity: 0; }
  }
  @keyframes __ol-fadein__ {
    from { opacity: 0; transform: translateY(14px); }
    to   { opacity: 1; transform: translateY(0); }
  }
  @keyframes __ol-slidedown__ {
    from { opacity: 0; transform: translateY(-110%); }
    to   { opacity: 1; transform: translateY(0); }
  }
  @keyframes __ol-slideup__ {
    from { opacity: 1; transform: translateY(0); }
    to   { opacity: 0; transform: translateY(-110%); }
  }
  @keyframes __ol-spin__ {
    to { transform: rotate(360deg); }
  }
  @keyframes __ol-barpulse__ {
    0%, 100% { opacity: 0.18; }
    50%       { opacity: 0.45; }
  }
`;

function injectKeyframes() {
  if (document.getElementById(STYLE_ID)) return;
  const style = document.createElement("style");
  style.id = STYLE_ID;
  style.textContent = KEYFRAMES;
  document.head.appendChild(style);
}

// ── Signal bars (all dead, gentle pulse) ──────────────────────────────────
function SignalBars() {
  const heights = [10, 14, 18, 22];
  return (
    <div
      style={{
        display: "flex",
        alignItems: "flex-end",
        justifyContent: "center",
        gap: 5,
        marginBottom: 36,
      }}
    >
      {heights.map((h, i) => (
        <div
          key={i}
          style={{
            width: 7,
            height: h,
            borderRadius: 4,
            background: "rgba(100,116,139,0.35)",
            border: "1px solid rgba(100,116,139,0.18)",
            animation: `__ol-barpulse__ 2.4s ease-in-out ${i * 0.18}s infinite`,
          }}
        />
      ))}
    </div>
  );
}

// ── WiFi-off SVG icon ──────────────────────────────────────────────────────
function WifiOffIcon() {
  return (
    <svg
      width={34}
      height={34}
      viewBox="0 0 24 24"
      fill="none"
      stroke="#ef4444"
      strokeWidth={1.75}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <line x1="1" y1="1" x2="23" y2="23" />
      <path d="M16.72 11.06A10.94 10.94 0 0 1 19 12.55" />
      <path d="M5 12.55a10.94 10.94 0 0 1 5.17-2.39" />
      <path d="M10.71 5.05A16 16 0 0 1 22.56 9" />
      <path d="M1.42 9a15.91 15.91 0 0 1 4.7-2.88" />
      <path d="M8.53 16.11a6 6 0 0 1 6.95 0" />
      <circle cx="12" cy="20" r="1" fill="#ef4444" stroke="none" />
    </svg>
  );
}

// ── Back-Online banner ─────────────────────────────────────────────────────
function BackOnlineBanner({ visible }) {
  return (
    <div
      aria-live="polite"
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        zIndex: 2147483647,
        display: "flex",
        justifyContent: "center",
        paddingTop: 18,
        pointerEvents: "none",
        animation: visible ? "__ol-slidedown__ 0.32s ease" : "__ol-slideup__ 0.28s ease",
        opacity: visible ? 1 : 0,
        transition: "opacity 0.28s ease",
      }}
    >
      <div
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 8,
          background: "linear-gradient(135deg,#059669,#10b981)",
          color: "#fff",
          padding: "10px 22px",
          borderRadius: 100,
          fontSize: 14,
          fontWeight: 600,
          fontFamily: "system-ui,-apple-system,sans-serif",
          letterSpacing: "-0.01em",
          boxShadow: "0 4px 24px rgba(16,185,129,0.45), 0 1px 4px rgba(0,0,0,0.2)",
        }}
      >
        <svg
          width={15}
          height={15}
          fill="none"
          viewBox="0 0 24 24"
          stroke="#fff"
          strokeWidth={2.8}
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M5 13l4 4L19 7" />
        </svg>
        Back Online
      </div>
    </div>
  );
}

// ── Main OfflineOverlay ────────────────────────────────────────────────────
export default function OfflineOverlay() {
  const { isOnline, isChecking, retryConnection } = useNetworkStatus();
  const [showBanner, setShowBanner] = useState(false);
  const [wasOffline, setWasOffline] = useState(false);
  const bannerTimer = useRef(null);

  useEffect(() => {
    injectKeyframes();
  }, []);

  // Lock body scroll when overlay is visible — prevents scroll-through on mobile
  useEffect(() => {
    if (!isOnline) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      // iOS Safari needs this on the html element too
      document.documentElement.style.overflow = "hidden";
      return () => {
        document.body.style.overflow = prev;
        document.documentElement.style.overflow = "";
      };
    }
  }, [isOnline]);

  useEffect(() => {
    if (!isOnline) {
      setWasOffline(true);
      setShowBanner(false);
      clearTimeout(bannerTimer.current);
    } else if (wasOffline) {
      setWasOffline(false);
      setShowBanner(true);
      clearTimeout(bannerTimer.current);
      bannerTimer.current = setTimeout(() => setShowBanner(false), 3000);
    }
    return () => clearTimeout(bannerTimer.current);
  }, [isOnline]);  // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <>
      {/* ── Back online toast ── */}
      {showBanner && <BackOnlineBanner visible={showBanner} />}

      {/* ── Offline overlay ── */}
      {!isOnline && (
        <div
          role="alertdialog"
          aria-modal="true"
          aria-label="You are offline"
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            // Explicit 100% dimensions for Android WebView + iOS Safari
            width: "100%",
            height: "100%",
            // 2147483647 = max int32 — sits above CRA's dev error overlay iframe
            zIndex: 2147483647,
            background: "rgba(2,6,23,0.96)",
            backdropFilter: "blur(6px)",
            WebkitBackdropFilter: "blur(6px)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 24,
            overscrollBehavior: "none",
            WebkitOverflowScrolling: "touch",
            animation: "__ol-fadein__ 0.25s ease",
          }}
        >
          <div
            style={{
              width: "100%",
              maxWidth: 440,
              textAlign: "center",
              animation: "__ol-fadein__ 0.35s ease 0.05s both",
            }}
          >
            {/* ── Pulsing icon ── */}
            <div
              style={{
                position: "relative",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                marginBottom: 32,
              }}
            >
              <div
                style={{
                  position: "absolute",
                  width: 80,
                  height: 80,
                  borderRadius: "50%",
                  background: "rgba(239,68,68,0.12)",
                  animation: "__ol-ping2__ 2.2s cubic-bezier(0,0,0.2,1) infinite",
                }}
              />
              <div
                style={{
                  position: "absolute",
                  width: 72,
                  height: 72,
                  borderRadius: "50%",
                  background: "rgba(239,68,68,0.18)",
                  animation: "__ol-ping1__ 2.2s cubic-bezier(0,0,0.2,1) 0.35s infinite",
                }}
              />
              <div
                style={{
                  position: "relative",
                  width: 76,
                  height: 76,
                  borderRadius: "50%",
                  background: "rgba(239,68,68,0.1)",
                  border: "1px solid rgba(239,68,68,0.28)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <WifiOffIcon />
              </div>
            </div>

            {/* ── Heading ── */}
            <h1
              style={{
                color: "#f1f5f9",
                fontSize: 26,
                fontWeight: 700,
                letterSpacing: "-0.03em",
                margin: "0 0 10px",
                fontFamily: "system-ui,-apple-system,sans-serif",
              }}
            >
              You're Offline
            </h1>

            {/* ── Subtext ── */}
            <p
              style={{
                color: "#94a3b8",
                fontSize: 15,
                lineHeight: 1.65,
                margin: "0 0 6px",
                fontFamily: "system-ui,-apple-system,sans-serif",
              }}
            >
              No internet connection detected.
            </p>
            <p
              style={{
                color: "#475569",
                fontSize: 13,
                lineHeight: 1.55,
                margin: "0 0 36px",
                fontFamily: "system-ui,-apple-system,sans-serif",
              }}
            >
              Check your Wi-Fi or mobile data and try again.
              <br />
              Your work is safe and will sync when you reconnect.
            </p>

            {/* ── Dead signal bars ── */}
            <SignalBars />

            {/* ── Try Again button ── */}
            <button
              type="button"
              onClick={retryConnection}
              disabled={isChecking}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 8,
                padding: "13px 30px",
                borderRadius: 12,
                background: isChecking
                  ? "rgba(255,255,255,0.06)"
                  : "rgba(255,255,255,0.97)",
                color: isChecking ? "#64748b" : "#0f172a",
                fontSize: 14,
                fontWeight: 600,
                border: "1px solid rgba(255,255,255,0.1)",
                cursor: isChecking ? "not-allowed" : "pointer",
                fontFamily: "system-ui,-apple-system,sans-serif",
                letterSpacing: "-0.01em",
                boxShadow: isChecking
                  ? "none"
                  : "0 4px 20px rgba(255,255,255,0.1), 0 1px 3px rgba(0,0,0,0.3)",
                transition: "background 0.15s, box-shadow 0.15s",
              }}
              onMouseEnter={(e) => {
                if (!isChecking) {
                  e.currentTarget.style.background = "rgba(255,255,255,1)";
                  e.currentTarget.style.boxShadow =
                    "0 6px 28px rgba(255,255,255,0.14), 0 1px 3px rgba(0,0,0,0.3)";
                }
              }}
              onMouseLeave={(e) => {
                if (!isChecking) {
                  e.currentTarget.style.background = "rgba(255,255,255,0.97)";
                  e.currentTarget.style.boxShadow =
                    "0 4px 20px rgba(255,255,255,0.1), 0 1px 3px rgba(0,0,0,0.3)";
                }
              }}
            >
              {isChecking ? (
                <>
                  <span
                    style={{
                      width: 14,
                      height: 14,
                      borderRadius: "50%",
                      border: "2px solid rgba(100,116,139,0.3)",
                      borderTopColor: "#64748b",
                      display: "inline-block",
                      animation: "__ol-spin__ 0.6s linear infinite",
                      flexShrink: 0,
                    }}
                  />
                  Checking connection…
                </>
              ) : (
                <>
                  <svg
                    width={15}
                    height={15}
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={2.5}
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M23 4v6h-6M1 20v-6h6" />
                    <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
                  </svg>
                  Try Again
                </>
              )}
            </button>

            {/* ── Footer note ── */}
            <p
              style={{
                marginTop: 28,
                color: "#1e293b",
                fontSize: 12,
                fontFamily: "system-ui,-apple-system,sans-serif",
                letterSpacing: "0.01em",
              }}
            >
              Page will automatically reload once connection is restored.
            </p>
          </div>
        </div>
      )}
    </>
  );
}
